import FocusManager from '../utils/focus-manager';
import ScrollManager from '../utils/scroll-manager';

export default function initHeaderNavigation() {
    new Header(document.getElementById('main-header'));
}

/**
 * @param {HTMLElement}       $el                                  HTML Element header
 * @param {HTMLElement}       $body                                HTML Body
 * @param {HTMLButtonElement} $menuToggler                         Toggler du menu
 * @param {HTMLElement}       $mainHeaderMenu                       Le menu principal
 * @param {HTMLElement}       $mainHeaderSubmenuBtnReturn          Le menu retour pour mobile
 * @param {HTMLElement}       $btnOpenSearch                       Le bouton pour faire apparaitre la barre de recherche en desktop
 * @param {integer}           mobileBreakpoint                     Breakpoint mobile
 * @param {string}            initializedDevice                    Nom du device initialisé (mobile | desktop)
 * @param {Element[]}         $openedPanels                        Liste des panneaux ouverts
 * 
 */
class Header {
    $el : HTMLElement | null;
    $body : HTMLElement | null;
    $menuToggler : HTMLButtonElement | null | undefined;
    $mainHeaderNav : HTMLElement | null | undefined;
    $mainHeaderMenu : HTMLElement | null | undefined;
    $mainHeaderSubmenuBtnReturn : NodeListOf<HTMLElement> | null | undefined;
    mobileBreakpoint : number;
    initializedDevice : string | null;
    $isAnchorNav : HTMLElement | null | undefined;
    $openedPanels : Element[];
    $activeButtonSubMenu : HTMLButtonElement | null;

    // Références stables des handlers bindés pour add/removeEventListener
    private _boundMobileClickMenuTogglerHandler: () => void;
    private _boundMobileKeyDownDocumentHandler: (evt: KeyboardEvent) => void;
    private _boundMobileClickMainHeaderNavLinkWithChildrenHandler: (evt: Event) => void;
    private _boundHandleDocumentKeyDown: (evt: KeyboardEvent) => void;
    private _boundHandleSubMenuKeyDown: EventListener;
    private _boundInitStickyNavigation: () => void;

    constructor($el : HTMLElement | null) {
        this.$el = $el;
        this.$body = document.querySelector('body');
        this.$menuToggler = $el?.querySelector('.js-main-header-burger-button');
        this.$mainHeaderNav = $el?.querySelector('.js-main-header-navigation');
        this.$mainHeaderMenu = $el?.querySelector('.js-main-header-menu');
        this.$mainHeaderSubmenuBtnReturn = $el?.querySelectorAll('.js-btn-submenu-return');
        this.mobileBreakpoint = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--breakpoint-main-header')) + 1;
        this.initializedDevice = null;
        this.$openedPanels = [];
        this.$activeButtonSubMenu = null;

        // Binding une seule fois pour garantir des références identiques entre add et remove
        this._boundMobileClickMenuTogglerHandler = this.mobileClickMenuTogglerHandler.bind(this);
        this._boundMobileKeyDownDocumentHandler = this.mobileKeyDownDocumentHandler.bind(this);
        this._boundMobileClickMainHeaderNavLinkWithChildrenHandler = this.mobileClickMainHeaderNavLinkWithChildrenHandler.bind(this);
        this._boundHandleDocumentKeyDown = this.handleDocumentKeyDown.bind(this);
        this._boundHandleSubMenuKeyDown = ((event: Event) => this.handleSubMenuKeyDown(event as KeyboardEvent)) as EventListener;
        this._boundInitStickyNavigation = this.initStickyNavigation.bind(this);

        this.init();
        window.addEventListener('resize', this.init.bind(this));

        // Listener scroll enregistré une seule fois ici, jamais dans init()
        window.addEventListener('scroll', this._boundInitStickyNavigation);
    }

    /**
     * Initialise le header dans le bon device en fonction
     * de la taille de l'écran (mobile ou desktop)
     *
     * @return {void}
     */
    init() {
        if (window.innerWidth < this.mobileBreakpoint && this.initializedDevice !== 'mobile') {
            this.initMobile();
        } else if (window.innerWidth >= this.mobileBreakpoint && this.initializedDevice !== 'desktop') {
            this.initDesktop();
        }

        this.initStickyNavigation();
    }

    /**
     * Initialise le header mobile
     *
     * @return {void}
     */
    initMobile() {
        this.initializedDevice = 'mobile';
        this.destroyDesktop();

        // Ouverture / Fermeture du menu au clique sur le toggler du menu
        if (this.$menuToggler) {
            this.$menuToggler.addEventListener('click', this._boundMobileClickMenuTogglerHandler);
        }
        // Fermeture du menu à l'appuie sur la touche Escape
        document.addEventListener('keydown', this._boundMobileKeyDownDocumentHandler);

        // Fermeture sous-menu au clique sur les boutons retour
        if (this.$mainHeaderSubmenuBtnReturn) {
            this.$mainHeaderSubmenuBtnReturn?.forEach($link => {
                $link.addEventListener('click', this.mobileClickMainHeaderPhoneNavHandler);
            });
        }

        // Ouverture sous-menu au clique sur un élément de menu contenant un sous-menu
        this.$mainHeaderMenu?.querySelectorAll('.menu-item-has-submenu').forEach($link => {
            $link.addEventListener('click', this._boundMobileClickMainHeaderNavLinkWithChildrenHandler);
        });
    }

    /**
     * Fonction à éxécuter au clique sur le toggler du menu
     *
     * @return {void}
     */
    mobileClickMenuTogglerHandler() {
        if (this.$el?.classList.contains('open')) {
            this.closeMobileMenu();
        } else {
            this.openMobileMenu();
        }
    }

    /**
     * Fonction à éxécuter à l'appuie sur une touche du clavier
     *
     * @return {void}
     */
    mobileKeyDownDocumentHandler(evt: KeyboardEvent) {
        if (evt.key === 'Escape' && this.$el?.classList.contains('open')) {
            this.closeMobileMenu(true);
        }
    }

    /**
     * Fonction à éxécuter au clique sur un lien du menu contenant des enfants
     *
     * @return {void}
     */
    mobileClickMainHeaderNavLinkWithChildrenHandler(evt: Event) {
        evt.preventDefault();
        const $item = (evt.target as Element)?.nextElementSibling;

        if ($item) {
            this.openMobileSubmenu($item);
        }
    }

    /**
     * Fonction à éxécuter au clique sur un lien de retour
     *
     * @return {void}
     */
    mobileClickMainHeaderPhoneNavHandler = () => {
        this.closeMobileMenu(true);
    }

    /**
     * Initialise le header desktop
     *
     * @return {void}
     */
    initDesktop() {
        this.destroyMobile();
        this.initializedDevice = 'desktop';

        const $subMenusBtns = this.$el?.querySelectorAll('.menu-item-has-submenu');

        // Ajout des écouteurs d'événements
        $subMenusBtns?.forEach((subMenuBtn) => {
            if (subMenuBtn) {
                subMenuBtn?.addEventListener('keydown', this._boundHandleSubMenuKeyDown);
            }
        });

        document.addEventListener('keydown', this._boundHandleDocumentKeyDown);
    }
    
    /**
     * Initialise les event clavier sur le menu desktop
     *
     * @return {void}
     */
    handleSubMenuKeyDown(event: KeyboardEvent) {
        const subMenuBtn = event.target as HTMLButtonElement;
        const keyboardEvent = event as KeyboardEvent;
        if (
            keyboardEvent.key === ' ' ||
            keyboardEvent.key === 'Spacebar' ||
            keyboardEvent.keyCode === 32 ||
            keyboardEvent.keyCode === 13 ||
            keyboardEvent.key === 'Enter'
        ) {
            keyboardEvent.preventDefault();
            if (subMenuBtn !== this.$activeButtonSubMenu) {
                if (this.$activeButtonSubMenu) {
                    this.$activeButtonSubMenu.classList.remove('active');
                }
                subMenuBtn?.classList.add('active');
                this.$activeButtonSubMenu = subMenuBtn;
            } else {
                subMenuBtn?.classList.remove('active');
                this.$activeButtonSubMenu = null;
            }
        }
    }

    /**
     * Initialise les event clavier sur le bouton esc pour fermer les sous menu
     *
     * @return {void}
     */
    handleDocumentKeyDown(event : KeyboardEvent) {
        const keyboardEvent = event as KeyboardEvent;
        const subMenusBtns = this.$el?.querySelectorAll('.menu-item-has-submenu');
        if (keyboardEvent.key === 'Escape' || keyboardEvent.key === 'Esc' || keyboardEvent.keyCode === 27) {
            subMenusBtns?.forEach((subMenuBtn) => {
                subMenuBtn.classList.remove('active');
                this.$activeButtonSubMenu = null;
            });
        }
    }

    /**
     * Détruit le header mobile
     *
     * @return {void}
     */
    destroyMobile() {
        // On ré-autorise le scroll dans la page
        ScrollManager.preventScroll(false);

        // On enlève la contrainte du focus
        FocusManager.untrapFocus();

        // On ferme le menu s'il était ouvert pour éviter les erreurs
        this.$el?.classList.remove('open');
        this.$body?.classList.remove('menu-is-open');

        // Suppression des events bind lors de l'initialisation du header mobile
        if (this.$menuToggler) {
            this.$menuToggler.removeEventListener('click', this._boundMobileClickMenuTogglerHandler);
        }

        document.removeEventListener('keydown', this._boundMobileKeyDownDocumentHandler);

        if (this.$mainHeaderSubmenuBtnReturn) {
            this.$mainHeaderSubmenuBtnReturn?.forEach($link => {
                $link.removeEventListener('click', this.mobileClickMainHeaderPhoneNavHandler);
            });
        }

        this.$mainHeaderMenu?.querySelectorAll('.menu-item-has-submenu').forEach($link => {
            $link.removeEventListener('click', this._boundMobileClickMainHeaderNavLinkWithChildrenHandler);
        });

        this.$mainHeaderMenu?.querySelectorAll('.menu-item-submenu.open').forEach($item => {
            $item.classList.remove('open');
        });
    }

    /**
     * Détruit le header desktop
     *
     * @return {void}
     */
    destroyDesktop() {
        const $subMenusBtns = this.$el?.querySelectorAll('.menu-item-has-submenu');

        // Suppression des écouteurs d'événements
        $subMenusBtns?.forEach((subMenuBtn) => {
            if (subMenuBtn) {
                subMenuBtn?.removeEventListener('keydown', this._boundHandleSubMenuKeyDown);
            }
        });

        document.removeEventListener('keydown', this._boundHandleDocumentKeyDown);
    }

    /**
     * Initialise le header sticky
     *
     * @return {void}
     */
    initStickyNavigation() {
        if (window.pageYOffset > 20) {
            this.$el?.classList.add('main-header--sticky');
            this.$body?.classList.add('main-header--is-sticky');
        } else {
            this.$el?.classList.remove('main-header--sticky');
            this.$body?.classList.remove('main-header--is-sticky');
        }
    }

    /**
     * Ouvre le menu mobile
     *
     * @return {void}
     */
    openMobileMenu() {
        this.$el?.classList.add('open');
        this.$body?.classList.add('menu-is-open');

        ScrollManager.preventScroll(true);
        
        FocusManager.trapFocus(
            [this.$el]
        );
    }

    /**
     * Ferme le menu mobile
     *
     * @param {boolean} onlyLastPanel Seulement le dernier panneau ouvert
     * @return {void}
     */
    closeMobileMenu(onlyLastPanel: boolean = false): void {
        if (onlyLastPanel && this.$openedPanels.length > 0) {
            this.closeMobileSubmenu();
        } else {
            this.$el?.classList.remove('open');
            this.$mainHeaderNav?.classList.remove('submenu-is-open');
            this.$body?.classList.remove('menu-is-open');

            this.$openedPanels.forEach($panel => {
                $panel.classList.remove('open');
            });
            this.$openedPanels = [];

            // On remet le scroll de la page
            ScrollManager.preventScroll(false);

            // On enlève la contrainte du focus
            FocusManager.untrapFocus();
        }
    }

    /**
     * Ouvre un sous-menu mobile
     *
     * @param {Element} $item
     * @return {void}
     */
    openMobileSubmenu($item : Element) {
        $item?.classList.add('open');

        this.$openedPanels?.push($item);

        this.$mainHeaderNav?.classList.add('submenu-is-open');
    }

    /**
     * Ferme le dernier sous-menu mobile ouvert
     *
     * @return {void}
     */
    closeMobileSubmenu() {
        const $item = this.$openedPanels?.pop();
        $item?.classList.remove('open');

        // On retire la classe uniquement si tous les panneaux sont fermés
        if (this.$openedPanels.length === 0) {
            this.$mainHeaderNav?.classList.remove('submenu-is-open');
        }
    }
}
